/**
 * Utilidad para reproducir efectos de sonido en la aplicación
 */

// URLs de sonidos de completado (puedes reemplazar con archivos locales o CDN)
const COMPLETION_SOUND_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuByO/bfzYIFmS57OmJMQgYZbzr5pNJCwxRp+PvtmMcBjiQ1/HIeisFI3fH8N+QQgUSerTp65VECg=='

// Contexto de audio Web API
let audioContext: AudioContext | null = null;

/**
 * Inicializar contexto de audio (solo una vez)
 */
function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  
  // Reanudar si está suspendido (navegadores modernos suspenden hasta interacción)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  
  return audioContext;
}

/**
 * Reproducir sonido de completado usando oscillator (síntesis de audio)
 * Crea un sonido agradable de "ding" sin necesidad de archivos externos
 */
export function playCompletionSound(): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    // Crear oscillator (generador de tono)
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    // Conectar nodos: oscillator -> gain -> salida
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Configurar tono: "ding" agradable
    oscillator.type = 'sine'; // Onda sinusoidal suave
    oscillator.frequency.setValueAtTime(800, now); // Nota alta (Mi)
    
    // Envelope: fade in rápido, fade out suave
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01); // Fade in rápido
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.5); // Fade out suave
    
    // Iniciar y detener
    oscillator.start(now);
    oscillator.stop(now + 0.5);
    
  } catch (error) {
    console.warn('No se pudo reproducir sonido de completado:', error);
  }
}

/**
 * Reproducir sonido de éxito (doble ding)
 * Para completados especiales o logros importantes
 */
export function playSuccessSound(): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    // Primer tono
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(600, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.2, now + 0.01);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
    osc1.start(now);
    osc1.stop(now + 0.3);
    
    // Segundo tono (más alto)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(800, now + 0.15);
    gain2.gain.setValueAtTime(0, now + 0.15);
    gain2.gain.linearRampToValueAtTime(0.25, now + 0.16);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    osc2.start(now + 0.15);
    osc2.stop(now + 0.5);
    
  } catch (error) {
    console.warn('No se pudo reproducir sonido de éxito:', error);
  }
}

/**
 * Reproducir sonido de alarma (doble tono alternante tipo alarma)
 * Usado cuando se alcanza el tiempo objetivo o se agota el tiempo límite
 */
export function playGoalReachedSound(): void {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    // Alarma: Tres tonos alternantes (alto-bajo-alto)
    const frequencies = [880, 659.25, 880]; // A5, E5, A5 - patrón de alarma
    
    frequencies.forEach((freq, index) => {
      const startTime = now + (index * 0.15);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'square'; // Onda cuadrada para sonido más de alarma
      osc.frequency.setValueAtTime(freq, startTime);
      
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.01); // Más volumen
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.25);
      
      osc.start(startTime);
      osc.stop(startTime + 0.25);
    });
  } catch (error) {
    console.warn('No se pudo reproducir sonido de alarma:', error);
  }
}

/**
 * Verificar si el sonido está habilitado en preferencias del usuario
 */
export function isSoundEnabled(): boolean {
  try {
    const soundMode = localStorage.getItem('sound_mode');
    return soundMode !== 'false'; // Por defecto está habilitado
  } catch {
    return true;
  }
}

/**
 * Reproducir sonido solo si está habilitado
 */
export function playSoundIfEnabled(soundFn: () => void): void {
  if (isSoundEnabled()) {
    soundFn();
  }
}

/**
 * Inicializar AudioContext (llamar al interactuar con UI)
 * Esto es necesario porque navegadores modernos bloquean audio hasta que haya interacción
 */
export function initAudioContext(): void {
  try {
    const ctx = getAudioContext();
    
    // Reproducir un silencio breve para "despertar" el audio
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0, ctx.currentTime); // Volumen 0 = silencio
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.001); // 1ms de silencio
  } catch {
    // Silently fail
  }
}
